import { useMemo, useState } from "react";

import { Alert, Collapse, IconButton, Menu, Tooltip } from "@mui/material";
import { CalendarIcon } from "@mui/x-date-pickers";

import { useQueryClient } from "@tanstack/react-query";
import { addDays, format, isSameDay, subDays } from "date-fns";
import { useAppDispatch } from "src/redux/hooks";
import { setClearAllErrors } from "src/redux/slices/ordering/orderSetSlice";

import { DatePicker } from "@components/Forms/DefaultInputs";
import {
  budgetYearOverrideForDate,
  isAllowedBudgetYear,
  isBudgetYearMissing,
  isJanuary,
} from "@features/budgets/budgetYearOverride";
import BudgetYearOverrideSelect from "@features/budgets/components/BudgetYearOverrideSelect";
import { getFiscalYear, yearToFY } from "@features/budgets/fiscalYear";
import { budgetSpendKeyFactory } from "@features/budgets/queries";
import { Button, FaIcon, Type } from "@features/ui";
import { OrderSetType } from "@models/OrderSet";
import { OrderSetVariant } from "@models/OrderSetVariant";
import { formatLocalDate, getLocalDate } from "@utils/date";
import { cn } from "@utils/tailwind";

import { orderSetsKeyFactory } from "../queries";
import useUpdateOrderSetVariantMutation from "./data/mutations/useUpdateOrderSetVariantMutation";
import useOrderSetId from "./data/useOrderSetId";

const OrderSetVariantRequestedDate = ({
  orderSetVariant,
  orderSetType,
}: {
  orderSetType: OrderSetType;
  orderSetVariant: OrderSetVariant;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (orderSetType === "pre-order") {
    // Pre-order variants follow their Program's budget year; display only.
    return (
      !!orderSetVariant.budgetYearOverride && (
        <Tooltip title="Budget year follows the Program">
          <span className="text-xs text-neutral-500 px-2 py-1">
            {orderSetVariant.budgetYearOverride}
          </span>
        </Tooltip>
      )
    );
  }
  return (
    <>
      <Tooltip
        title={
          orderSetVariant.inMarketDate
            ? `Requested Date: ${formatLocalDate(orderSetVariant.inMarketDate, "P")}`
            : "Set Requested Date"
        }
      >
        <IconButton
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
          }}
          className={cn(
            "rounded-full text-primary-800 flex items-center gap-1",
            orderSetVariant.inMarketDate &&
              "bg-primary-200/30 hover:bg-primary-200/50"
          )}
        >
          <CalendarIcon
            fontSize="small"
            className={cn(
              "text-neutral-500",
              orderSetVariant.inMarketDate && "text-primary-500"
            )}
          />
          {orderSetVariant.inMarketDate && (
            <span className="text-xs">
              {formatLocalDate(orderSetVariant.inMarketDate, "MMM d")}
            </span>
          )}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: { className: "py-4 px-6 w-full max-w-sm" },
        }}
      >
        <DeliveryDateMenu
          orderSetVariant={orderSetVariant}
          orderSetType={orderSetType}
          handleClose={() => setAnchorEl(null)}
        />
      </Menu>
    </>
  );
};

const DeliveryDateMenu = ({
  orderSetVariant,
  orderSetType,
  handleClose,
}: {
  orderSetVariant: OrderSetVariant;
  orderSetType: "on-demand" | "from-inventory";
  handleClose: () => void;
}) => {
  const dispatch = useAppDispatch();
  const orderSetId = useOrderSetId();
  const queryClient = useQueryClient();
  const updateOrderSetVariant = useUpdateOrderSetVariantMutation();

  const defaultDate = getLocalDate(orderSetVariant.estimatedInMarketDate);
  const savedDate = getLocalDate(
    orderSetVariant.inMarketDate ?? orderSetVariant.estimatedInMarketDate
  );
  const [deliveryDate, setDeliveryDate] = useState(savedDate);

  const [budgetYearOverride, setBudgetYearOverride] = useState(
    orderSetVariant.budgetYearOverride ?? ""
  );

  // The chosen January fiscal year wins over the date-derived one.
  const budgetYearFor = (date: Date, override: string | null | undefined) =>
    budgetYearOverrideForDate(date, override) ?? yearToFY(getFiscalYear(date));
  const yearChanged =
    budgetYearFor(deliveryDate, budgetYearOverride) !==
    budgetYearFor(savedDate, orderSetVariant.budgetYearOverride);
  const budgetYearMissing = isBudgetYearMissing(
    deliveryDate,
    budgetYearOverride
  );

  const boundary = useMemo(() => {
    if (orderSetType === "from-inventory") {
      return {
        min: subDays(defaultDate, 3),
        max: addDays(defaultDate, 90),
      };
    }
    return {
      min: subDays(defaultDate, orderSetVariant.leadTime >> 1),
      max: addDays(defaultDate, 60),
    };
  }, [orderSetType, defaultDate, orderSetVariant.leadTime]);

  const handleSave = () => {
    const revalidateBudgets = yearChanged;
    updateOrderSetVariant.mutate(
      {
        id: orderSetVariant.id,
        // A January date is always sent explicitly, in the same request as
        // its budget year.
        inMarketDate:
          !isSameDay(deliveryDate, defaultDate) || isJanuary(deliveryDate)
            ? format(deliveryDate, "yyyy-MM-dd")
            : null,
        ...(isJanuary(deliveryDate) && { budgetYearOverride }),
      },
      {
        onSuccess: () => {
          if (revalidateBudgets) {
            // Invalidate all budgets
            queryClient.invalidateQueries({
              queryKey: budgetSpendKeyFactory._def,
            });
            queryClient.invalidateQueries({
              queryKey:
                orderSetsKeyFactory.detail(orderSetId)._ctx.orders.queryKey,
            });
            dispatch(setClearAllErrors(true));
          }
          handleClose();
        },
      }
    );
  };
  return (
    <div className="space-y-4">
      <div>
        <Type.H2>Edit Requested Date</Type.H2>
        <Type.P className="mt-2">
          Every effort will be made to hit your requested date, but we cannot
          guarantee it.
        </Type.P>
      </div>

      <div className="flex flex-col gap-2 mt-4">
        <Type.Small>
          Standard Delivery Date:{" "}
          {formatLocalDate(orderSetVariant.estimatedInMarketDate, "MM/dd/yyyy")}
        </Type.Small>
        <Type.Small>Item Lead Time: {orderSetVariant.leadTime} days</Type.Small>
      </div>
      <div className="flex gap-2 items-center">
        <DatePicker
          value={deliveryDate}
          onChange={(v) => {
            setDeliveryDate(v ?? defaultDate);
            // Never carry a choice over to a date it is not allowed for.
            if (!isAllowedBudgetYear(v ?? defaultDate, budgetYearOverride)) {
              setBudgetYearOverride("");
            }
          }}
          minDate={boundary.min}
          maxDate={boundary.max}
          className="flex-1"
        />
        <IconButton
          onClick={() => setDeliveryDate(defaultDate)}
          className="flex-none"
        >
          <FaIcon icon="rotate-left" variant="solid" className="text-base" />
        </IconButton>
      </div>
      <BudgetYearOverrideSelect
        date={deliveryDate}
        value={budgetYearOverride}
        onChange={setBudgetYearOverride}
        required
      />
      {updateOrderSetVariant.error && (
        <Alert severity="error">{updateOrderSetVariant.error.message}</Alert>
      )}
      <Collapse in={yearChanged}>
        <Alert severity="info">
          Updating budgets may set item quantities to 0 if the requested
          quantity exceeds the budget. Make sure to check your orders after
          saving.
        </Alert>
      </Collapse>
      <Button
        variant="contained"
        fullWidth
        onClick={handleSave}
        loading={updateOrderSetVariant.isPending}
        disabled={
          budgetYearMissing ||
          (isSameDay(savedDate, deliveryDate) &&
            budgetYearOverride === (orderSetVariant.budgetYearOverride ?? ""))
        }
      >
        {yearChanged ? "Save and update budgets" : "Save"}
      </Button>
    </div>
  );
};

export default OrderSetVariantRequestedDate;
